import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { scaleSqrt } from 'd3-scale';
import { MapPin, Building2, Calendar, Info, Layers, X, Plus, Minus, Maximize2 } from 'lucide-react';
import { getMapLocations } from '../api';
import { getApiErrorMessage } from '../utils/apiError';
import { coordsFor } from '../data/ukCityCoords';
import { cityInfo, REGION_COLORS, UK_REGIONS } from '../data/cityReference';
import ukGeo from '../data/ukGeo.json';
import { compactNumber } from '../utils/format';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton } from '../components/ui/Skeleton';

function MapView() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hover, setHover] = useState(null);
  const [region, setRegion] = useState('');
  const [showLegend, setShowLegend] = useState(true);
  // Zoom/pan state for the map. center is [lng, lat]; zoom is a multiplier.
  const DEFAULT_VIEW = { coordinates: [-3.2, 55.2], zoom: 1 };
  const [view, setView] = useState(DEFAULT_VIEW);

  useEffect(() => {
    document.title = 'Map | Dental Events';
    return () => { document.title = 'Dental Events'; };
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getMapLocations()
      .then((res) => { if (active) { setRows(res.data.data); setError(null); } })
      .catch((err) => { if (active) setError(getApiErrorMessage(err, 'Failed to load map data')); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const { points, unmapped } = useMemo(() => {
    const pts = [];
    const missing = [];
    for (const r of rows) {
      const c = coordsFor(r.location);
      const info = cityInfo(r.location);
      if (c) pts.push({ ...r, ...c, region: info?.region || null, info });
      else missing.push(r.location);
    }
    return { points: pts, unmapped: missing };
  }, [rows]);

  const visible = region ? points.filter((p) => p.region === region) : points;
  const maxEvents = points.reduce((m, p) => Math.max(m, p.eventCount), 1);
  // Marker radius shrinks as you zoom in so bubbles don't swamp the map.
  const radius = scaleSqrt().domain([0, maxEvents]).range([4, 26]);
  const regionsPresent = UK_REGIONS.filter((rg) => points.some((p) => p.region === rg));
  const eventsMapped = visible.reduce((s, p) => s + p.eventCount, 0);

  // Fit the view to a set of points (used when a region is selected → regional zoom).
  const fitToPoints = useCallback((pts) => {
    if (!pts || pts.length === 0) return;
    const lngs = pts.map((p) => p.lng);
    const lats = pts.map((p) => p.lat);
    const minLng = Math.min(...lngs), maxLng = Math.max(...lngs);
    const minLat = Math.min(...lats), maxLat = Math.max(...lats);
    const center = [(minLng + maxLng) / 2, (minLat + maxLat) / 2];
    // Derive a zoom from the geographic span; clamp to a sensible range.
    const span = Math.max(maxLng - minLng, (maxLat - minLat) * 1.6, 0.5);
    const zoom = Math.min(8, Math.max(1.4, 7 / span));
    setView({ coordinates: center, zoom });
  }, []);

  // When the region filter changes, auto-zoom to that region (or reset for "All").
  useEffect(() => {
    if (!region) {
      setView(DEFAULT_VIEW);
      return;
    }
    const pts = points.filter((p) => p.region === region);
    if (pts.length > 0) fitToPoints(pts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [region, points, fitToPoints]);

  const clampZoom = (z) => Math.min(8, Math.max(1, z));
  const zoomBy = (factor) => setView((v) => ({ ...v, zoom: clampZoom(v.zoom * factor) }));
  const resetView = () => { setRegion(''); setView(DEFAULT_VIEW); };

  const panel = 'bg-white/90 backdrop-blur ring-1 ring-ink-200 shadow-card rounded-xl';

  return (
    <div className="space-y-3 animate-fade-in-up">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="page-title mb-0.5">Geographic footprint</h1>
          <p className="text-sm text-ink-500">Competitor events across the UK — coloured by region, sized by event count.</p>
        </div>
        {/* Region filter */}
        <select
          className="input w-auto max-w-[220px]"
          value={region}
          onChange={(e) => setRegion(e.target.value)}
          aria-label="Filter by region"
        >
          <option value="">All regions</option>
          {regionsPresent.map((rg) => <option key={rg} value={rg}>{rg}</option>)}
        </select>
      </div>

      {error && <div className="card bg-red-50 ring-red-200"><p className="text-red-700">{error}</p></div>}

      {loading ? (
        <Skeleton className="h-[70vh] min-h-[460px] w-full rounded-2xl" />
      ) : points.length === 0 ? (
        <EmptyState icon={MapPin} title="Nothing to map yet" message="No locations matched known UK coordinates." />
      ) : (
        <div className="relative w-full h-[calc(100vh-190px)] min-h-[460px] rounded-2xl ring-1 ring-ink-200 bg-gradient-to-b from-ink-50 to-white overflow-hidden shadow-card">
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{ center: [-3.2, 55.2], scale: 2300 }}
            width={800}
            height={620}
            style={{ width: '100%', height: '100%' }}
            preserveAspectRatio="xMidYMid meet"
          >
            <ZoomableGroup
              center={view.coordinates}
              zoom={view.zoom}
              minZoom={1}
              maxZoom={8}
              onMoveEnd={({ coordinates, zoom }) => setView({ coordinates, zoom })}
            >
              <Geographies geography={ukGeo}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill="#eef2f7"
                      stroke="#cbd5e1"
                      strokeWidth={0.5}
                      style={{ default: { outline: 'none' }, hover: { outline: 'none', fill: '#eef2f7' }, pressed: { outline: 'none' } }}
                    />
                  ))
                }
              </Geographies>
              {visible.map((p) => {
                const color = REGION_COLORS[p.region] || '#0d9488';
                // Counter-scale the radius/stroke by zoom so markers stay readable
                // (slightly shrinking as you zoom in) rather than ballooning.
                const r = radius(p.eventCount) / Math.sqrt(view.zoom);
                return (
                  <Marker
                    key={p.location}
                    coordinates={[p.lng, p.lat]}
                    onMouseEnter={() => setHover(p)}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => navigate(`/locations/${encodeURIComponent(p.location)}`)}
                    style={{ default: { cursor: 'pointer' }, hover: { cursor: 'pointer' } }}
                  >
                    <circle r={r} fill={color} fillOpacity={0.55} stroke={color} strokeWidth={1.2 / Math.sqrt(view.zoom)} />
                  </Marker>
                );
              })}
            </ZoomableGroup>
          </ComposableMap>

          {/* Zoom controls — top-right (above the unmapped note) */}
          <div className="absolute z-20 top-3 right-3 flex flex-col gap-1">
            <button
              onClick={() => zoomBy(1.5)}
              className={`${panel} w-8 h-8 flex items-center justify-center text-ink-700 hover:text-dental-700`}
              aria-label="Zoom in"
              title="Zoom in"
            >
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={() => zoomBy(1 / 1.5)}
              className={`${panel} w-8 h-8 flex items-center justify-center text-ink-700 hover:text-dental-700`}
              aria-label="Zoom out"
              title="Zoom out"
            >
              <Minus className="w-4 h-4" />
            </button>
            <button
              onClick={resetView}
              className={`${panel} w-8 h-8 flex items-center justify-center text-ink-700 hover:text-dental-700`}
              aria-label="Reset view"
              title="Reset view"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>

          {/* Hover detail — top-left */}
          {hover && (
            <div className={`absolute z-20 top-3 left-3 ${panel} px-3 py-2 text-sm pointer-events-none max-w-[230px]`}>
              <p className="font-semibold text-ink-900">{hover.location}</p>
              {hover.info && <p className="text-[11px] text-ink-400">{hover.info.district} · {hover.region}</p>}
              <div className="mt-1.5 space-y-0.5 text-xs text-ink-600">
                <p className="flex justify-between gap-4"><span>Events</span><span className="font-semibold text-dental-700">{hover.eventCount}</span></p>
                <p className="flex justify-between gap-4"><span>Clinics</span><span>{hover.clinicCount}</span></p>
                {hover.info?.population != null && <p className="flex justify-between gap-4"><span>Population</span><span>{compactNumber(hover.info.population)}</span></p>}
                {hover.info?.elderlyPercent != null && <p className="flex justify-between gap-4"><span>Aged 65+</span><span>{hover.info.elderlyPercent}%</span></p>}
                {hover.info?.dentists != null && <p className="flex justify-between gap-4"><span>Dentists~</span><span>{hover.info.dentists.toLocaleString()}</span></p>}
              </div>
              <p className="mt-1.5 text-[10px] text-ink-400">Click bubble to open city →</p>
            </div>
          )}

          {/* Legend — bottom-left */}
          {showLegend ? (
            <div className={`absolute z-10 bottom-3 left-3 ${panel} px-3 py-2 max-w-[200px]`}>
              <div className="flex items-center justify-between mb-1">
                <p className="text-[11px] font-semibold text-ink-600 inline-flex items-center gap-1"><Layers className="w-3 h-3" /> Regions</p>
                <button onClick={() => setShowLegend(false)} className="text-ink-400 hover:text-ink-700"><X className="w-3 h-3" /></button>
              </div>
              <div className="grid grid-cols-1 gap-0.5 max-h-[180px] overflow-auto pr-1">
                {regionsPresent.map((rg) => (
                  <button
                    key={rg}
                    onClick={() => setRegion(region === rg ? '' : rg)}
                    className={`flex items-center gap-1.5 text-[11px] text-left rounded px-1 py-0.5 hover:bg-ink-100 ${region === rg ? 'font-semibold text-ink-900' : 'text-ink-600'}`}
                  >
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: REGION_COLORS[rg] }} />
                    <span className="truncate">{rg}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <button onClick={() => setShowLegend(true)} className={`absolute z-10 bottom-3 left-3 ${panel} px-2.5 py-1.5 text-[11px] font-medium text-ink-600 inline-flex items-center gap-1`}>
              <Layers className="w-3 h-3" /> Legend
            </button>
          )}

          {/* Metrics — bottom-right */}
          <div className={`absolute z-10 bottom-3 right-3 ${panel} px-3 py-2.5 w-[180px]`}>
            <p className="text-[11px] font-semibold text-ink-500 mb-1.5">{region || 'All regions'}</p>
            <div className="space-y-1.5 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-ink-500 inline-flex items-center gap-1.5 text-xs"><MapPin className="w-3.5 h-3.5" /> Locations</span>
                <span className="font-bold text-ink-900">{visible.length}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className="text-ink-500 inline-flex items-center gap-1.5 text-xs"><Calendar className="w-3.5 h-3.5" /> Events</span>
                <span className="font-bold text-ink-900">{eventsMapped}</span>
              </div>
              <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-ink-100">
                <span className="text-ink-500 inline-flex items-center gap-1.5 text-xs"><Building2 className="w-3.5 h-3.5" /> Busiest</span>
                <span className="font-semibold text-ink-800 text-xs truncate max-w-[90px]">{visible[0]?.location || '—'}</span>
              </div>
            </div>
          </div>

          {/* Unmapped note — top-right, offset left of the zoom controls */}
          {unmapped.length > 0 && (
            <div className={`absolute z-10 top-3 right-14 ${panel} px-2.5 py-1.5 text-[11px] text-ink-500 max-w-[180px] inline-flex items-start gap-1.5`}>
              <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-ink-400" />
              <span>{unmapped.length} without coordinates</span>
            </div>
          )}

          {/* Zoom hint — bottom-center */}
          <div className="absolute z-10 bottom-3 left-1/2 -translate-x-1/2 text-[10px] text-ink-400 bg-white/70 rounded-full px-2.5 py-0.5 pointer-events-none">
            Scroll to zoom · drag to pan · pick a region to zoom in
          </div>
        </div>
      )}
    </div>
  );
}

export default MapView;
